import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Clock } from "lucide-react";
import { toast } from "sonner";

import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/v2/notifications?limit=100`);
      setItems(Array.isArray(r.data) ? r.data : (r.data.notifications || []));
    } catch (err) {
      toast.error("Could not load notifications");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const markAll = async () => {
    try { await axios.post(`${API}/api/v2/notifications/read-all`); refresh(); }
    catch (e) { toast.error("Mark-all failed"); }
  };
  const markOne = async (id) => {
    try { await axios.patch(`${API}/api/v2/notifications/${id}/read`); refresh(); }
    catch (e) { toast.error("Mark failed"); }
  };

  const unread = items.filter((i) => !i.is_read).length;

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" /> Notifications
          </h1>
          <p className="text-sm text-slate-500">
            {unread} unread of {items.length}
          </p>
        </div>
        <Button onClick={markAll} disabled={unread === 0} variant="outline">
          <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
        </Button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400 italic">
          No notifications. They'll appear when an approval is assigned to you,
          a checklist item is owned by you, or a bid stage transitions.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {items.map((n) => (
          <Card
            key={n.id}
            onClick={() => {
              if (!n.is_read) markOne(n.id);
              if (n.link) navigate(n.link);
            }}
            className={`cursor-pointer hover:shadow-sm transition ${
              n.is_read ? "bg-white" : "bg-blue-50/40 border-blue-200"
            }`}
          >
            <CardContent className="py-3 flex items-start gap-3">
              <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${n.is_read ? "bg-slate-200" : "bg-blue-500"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{n.title}</p>
                {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {n.created_at?.slice(0, 19).replace("T", " ")}
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {n.kind}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
